import { Box, BoxProps, Flex, Icon, Text, Tooltip } from "@chakra-ui/react";
import { PanelTopClose } from "lucide-react";
import React from "react";
import { LuVideoOff } from "react-icons/lu";

import { IconButton } from "../../../components";
import { ExtendedHTMLVideoElement } from "../../../hooks/usePictureInPicture";
import { CallbackRef, MediaListeners } from "../MediaPlayer/useMediaPlayer";
import { VideoCallbackRef } from "../MediaPlayer/useVideoState";
import {
  CONTROL_ACTIVE_BG,
  CONTROL_HOVER_BG,
  IconPictureInPicture,
} from "./theme";
import { useNoCameraOverlay } from "./useNoCameraOverlay";

export type VideoFullScreenControls = {
  isFullscreenSupported: boolean;
  isFullscreen: boolean;
  toggleFullscreen: (() => Promise<void> | undefined) | undefined;
};

type VideoPlayerV2Props = {
  height?: string | number;
  src?: string;
  mediaPlayerRef: CallbackRef;
  videoRef: VideoCallbackRef;
  isVideoVisible: boolean;
  pipActive?: boolean;
  currentTime?: number;
  cameraTimestamps?: Array<{ timestamp: number; enabled: boolean }>;
  onClick?: BoxProps["onClick"];
  /** Callback to exit PiP mode and return video to the main player */
  onExitPip?: () => void;
  containerStyle?: BoxProps;
  preload?: "none" | "metadata" | "auto";
  /** CSS object-fit for the video element (e.g., "cover" for no black bars) */
  objectFit?: React.CSSProperties["objectFit"];
} & MediaListeners;

const VideoPlayerV2: React.FC<VideoPlayerV2Props> = ({
  src,
  height,
  mediaPlayerRef,
  videoRef,
  isVideoVisible,
  pipActive,
  currentTime,
  cameraTimestamps = [],
  onClick,
  onExitPip,
  containerStyle,
  preload,
  objectFit,
  ...listeners
}) => {
  // Show PIP overlay when video is in PIP mode (video hidden but PIP active)
  const showPipOverlay = pipActive && !isVideoVisible;
  const [videoEl, setVideoEl] = React.useState<HTMLVideoElement | null>(null);
  const shouldShowNoCameraOverlay = useNoCameraOverlay({
    cameraTimestamps,
    currentTime,
    src,
    videoEl,
  });

  return (
    <Box
      bg="black"
      width="100%"
      position="relative"
      hidden={!isVideoVisible && !pipActive}
      onClick={onClick}
      height={height}
      sx={{
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
        textRendering: "optimizeLegibility",
        imageRendering: "smooth",
        transform: "translateZ(0)",
        willChange: "transform",
      }}
      {...containerStyle}
    >
      {showPipOverlay && (
        <Flex
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="gray.900"
          direction="column"
          align="center"
          justify="center"
          zIndex="1"
        >
          <Icon as={IconPictureInPicture} color="white" boxSize="16" mb="4" />
          <Text color="white" fontSize="md" fontWeight="medium" mb="4">
            This video is playing in Picture-in-Picture mode.
          </Text>
          {onExitPip && (
            <Tooltip label="Back to player">
              <IconButton
                icon={<Icon as={PanelTopClose} boxSize="6" />}
                aria-label="Back to player"
                borderRadius="full"
                w={10}
                h={10}
                minW={10}
                color="white"
                bg="blackAlpha.600"
                _hover={{ bg: CONTROL_HOVER_BG }}
                _active={{ bg: CONTROL_ACTIVE_BG }}
                onClick={(e) => {
                  e.stopPropagation();
                  onExitPip();
                }}
              />
            </Tooltip>
          )}
        </Flex>
      )}
      {shouldShowNoCameraOverlay && !showPipOverlay && (
        <Flex
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="black"
          direction="column"
          align="center"
          justify="center"
          zIndex="1"
          pointerEvents="none"
        >
          <Flex direction="column" align="center" justify="center">
            <Icon as={LuVideoOff} color="gray.500" boxSize="12" />
            <Text color="gray.600" fontSize="4xl" fontWeight="600" mt="4">
              Camera disabled
            </Text>
          </Flex>
        </Flex>
      )}
      <video
        onContextMenu={(e) => e.preventDefault()}
        disableRemotePlayback
        ref={(element: HTMLVideoElement | null) => {
          mediaPlayerRef(element);
          videoRef(element as ExtendedHTMLVideoElement | null);
          setVideoEl(element);
        }}
        playsInline
        controlsList="nodownload"
        preload={preload}
        src={src}
        {...listeners}
        style={
          {
            display: showPipOverlay ? "none" : "block",
            height,
            width: objectFit ? "100%" : undefined,
            objectFit,
            margin: "auto",
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            transform: "translateZ(0)",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
          } as React.CSSProperties
        }
      />
    </Box>
  );
};

export default VideoPlayerV2;
